/**
 * Structured report sink for the brand-skin pipeline.
 *
 * Every skin operation appends what it applied, preserved, skipped, or
 * flagged — nothing is silently dropped. The composer returns the report as
 * `importReport` beside the composed state; the wizard's preview step and the
 * export prepublish audit both speak this vocabulary.
 *
 * Worker-safe: pure data.
 */

export function createSkinReport() {
  return {
    applied: {
      sectionsSkinned: 0,
      textPairs: 0,
      photosAssigned: [],
      bindings: { services: 0, testimonials: 0, copy: [] }
    },
    preserved: [],
    skipped: [],
    needsUpload: [],
    needsReview: [],
    links: [],
    contamination: null
  };
}

/** Append an entry to a list bucket; no-ops when report is absent. */
export function reportPush(report, bucket, entry) {
  if (!report || !Array.isArray(report[bucket])) return;
  report[bucket].push(entry);
}

/**
 * SK-8. Count REAL replaced occurrences.
 *
 * `applied.textPairs` was initialised to 0 and never incremented, so the number
 * was a decoration. `applyOccurrences` has returned a truthful `replaced` count
 * since SK-3, so the data was already there and only the wiring was missing.
 *
 * Its sibling `applied.social` was DELETED rather than fixed: nothing has ever
 * written it, so `import-report.js`'s social sentence has not rendered once in
 * the product's life, and `report.links` already records link rewriting. A
 * field that has never once been true is dead weight, not a feature.
 */
export function reportTextReplacements(report, count) {
  if (!report || !count) return;
  report.applied.textPairs += count;
}

/** Record a client photo assignment (deduped by URL, counts groups). */
export function reportPhotoAssigned(report, url, sectionId) {
  if (!report) return;
  const existing = report.applied.photosAssigned.find((item) => item.url === url);
  if (existing) {
    existing.groups += 1;
  } else {
    report.applied.photosAssigned.push({ url, groups: 1, firstSectionId: sectionId });
  }
}
